/**
 * component-card-swatch-image.js
 * Post Mortem — CRO enhancement for product cards.
 *
 * Turns the card colour swatches and size options into a lightweight variant
 * picker, without leaving the collection:
 *   1) Colour swatch (click / hover) swaps the card image + points links to it.
 *   2) Size pills are selectable; colour + size resolve the exact variant, the
 *      card links update to it, and sizes sold-out for the chosen colour dim.
 *   3) The "+N" swatch reveal button expands hidden swatches in place.
 *
 * Minimalist, dependency-free, event-delegated. Degrades to plain variant
 * links when JS is off.
 */
(function () {
  'use strict';

  var CARD_SELECTOR = '.kt-card, [data-js-product-item]';
  var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
  var states = new WeakMap();

  function closest(el, selector) {
    while (el && el.nodeType === 1) {
      if (el.matches(selector)) return el;
      el = el.parentElement;
    }
    return null;
  }
  function getCard(el) { return closest(el, CARD_SELECTOR); }
  function getPrimaryImg(card) { return card ? card.querySelector('.kt-primary-image img') : null; }

  // --- Per-card state -------------------------------------------------------
  function state(card) {
    var s = states.get(card);
    if (!s) {
      s = { selColor: null, selSize: null, map: null, sizePos: 0, colorPos: 0, base: null };
      var sizesEl = card.querySelector('product-card-sizes');
      if (sizesEl) {
        s.sizePos = parseInt(sizesEl.getAttribute('data-size-position'), 10) || 0;
        s.colorPos = parseInt(sizesEl.getAttribute('data-color-position'), 10) || 0;
        s.base = sizesEl.getAttribute('data-url') || null;
        var script = sizesEl.querySelector('.kt-card-variant-map');
        if (script) { try { s.map = JSON.parse(script.textContent); } catch (e) { s.map = null; } }
      }
      var activeSwatch = card.querySelector('.kt-product-color-swatch.kt-product-variant__item--current[data-color]');
      if (activeSwatch) s.selColor = activeSwatch.getAttribute('data-color');
      states.set(card, s);
    }
    return s;
  }

  // --- Image swap -----------------------------------------------------------
  function baseSrc(card) {
    var img = getPrimaryImg(card);
    if (!img) return null;
    if (!img.dataset.baseSrc) {
      img.dataset.baseSrc = img.currentSrc || img.src;
      img.dataset.baseSrcset = img.getAttribute('srcset') || '';
    }
    return img;
  }
  function fileName(u) { return u ? (u.split('?')[0].split('/').pop() || '') : ''; }

  function applyImage(card, url, animate) {
    var img = baseSrc(card);
    if (!img || !url) return;
    // Skip if the card is already showing this exact image.
    if (fileName(img.currentSrc || img.src) === fileName(url)) return;
    if (!animate) {
      img.style.opacity = '1';
      img.setAttribute('srcset', '');
      img.src = url;
      return;
    }
    // Visible cross-fade when a colour is chosen.
    img.style.transition = 'opacity 0.25s ease';
    img.style.opacity = '0';
    window.setTimeout(function () {
      img.setAttribute('srcset', '');
      img.src = url;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { img.style.opacity = '1'; });
      });
    }, 170);
  }
  function revertImage(card) {
    var img = getPrimaryImg(card);
    if (!img || !img.dataset.baseSrc) return;
    img.style.opacity = '1';
    img.setAttribute('srcset', img.dataset.baseSrcset || '');
    img.src = img.dataset.baseSrc;
  }

  function setActive(el, sel) {
    var list = closest(el, '.kt-product-variant__list');
    if (list) list.querySelectorAll(sel).forEach(function (n) { n.classList.remove('kt-product-variant__item--current', 'kt-card-size-pill--active'); });
    if (el.classList.contains('kt-card-size-pill')) el.classList.add('kt-card-size-pill--active');
    else el.classList.add('kt-product-variant__item--current');
  }

  function updateCardLinks(card, url) {
    if (!card || !url) return;
    card.querySelectorAll('.kt-product-image__link, .kt-product-title a, .kt-card__link').forEach(function (a) {
      a.setAttribute('href', url);
    });
  }

  // --- Variant resolution ---------------------------------------------------
  function variantForColorSize(s, color, size) {
    if (!s.map) return null;
    return s.map.find(function (v) {
      var okColor = (s.colorPos > 0 && color) ? v.o[s.colorPos - 1] === color : true;
      return okColor && v.o[s.sizePos - 1] === size;
    }) || null;
  }

  function updateSizeAvailability(card) {
    var s = state(card);
    if (!s.map) return;
    card.querySelectorAll('.kt-card-size-pill').forEach(function (pill) {
      var size = pill.getAttribute('data-size');
      var avail;
      if (s.colorPos > 0 && s.selColor) {
        var v = variantForColorSize(s, s.selColor, size);
        avail = v ? v.a : false;
      } else {
        avail = s.map.some(function (v) { return v.o[s.sizePos - 1] === size && v.a; });
      }
      pill.classList.toggle('kt-card-size-pill--soldout', !avail);
      if (!avail) pill.setAttribute('disabled', ''); else pill.removeAttribute('disabled');
    });
  }

  function applyResolvedVariant(card) {
    var s = state(card);
    if (!s.selSize) return;
    var v = variantForColorSize(s, s.selColor, s.selSize);
    if (!v && !s.selColor) {
      // No colour chosen yet: first available variant for the size.
      v = s.map && (s.map.find(function (x) { return x.o[s.sizePos - 1] === s.selSize && x.a; }) ||
                    s.map.find(function (x) { return x.o[s.sizePos - 1] === s.selSize; }));
    }
    if (v) {
      var url = (s.base || '') + '?variant=' + v.id;
      updateCardLinks(card, url);
      if (v.img) applyImage(card, v.img, true);
    }
    updateQuickAdd(card, v);
  }

  // Reveal the add-to-cart button only when a complete, available variant is chosen.
  function updateQuickAdd(card, variant) {
    var btn = card.querySelector('[data-card-atc]');
    if (!btn) return;
    var s = state(card);
    var colorOk = s.colorPos > 0 ? !!s.selColor : true;
    var ready = colorOk && !!s.selSize && variant && variant.a;
    if (ready) {
      btn.dataset.variantId = variant.id;
      btn.hidden = false;
      btn.setAttribute('aria-hidden', 'false');
    } else {
      btn.hidden = true;
      btn.setAttribute('aria-hidden', 'true');
      delete btn.dataset.variantId;
    }
  }

  function addToCart(btn) {
    var id = btn.dataset.variantId;
    if (!id || btn.classList.contains('kt-button--loading')) return;
    btn.classList.add('kt-button--loading');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1 }] })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (res) {
        btn.classList.remove('kt-button--loading');
        if (!res.ok) return;
        if (typeof window.refreshCart === 'function') window.refreshCart(true);
        else window.location.href = '/cart';
      })
      .catch(function () {
        btn.classList.remove('kt-button--loading');
        window.location.href = '/cart';
      });
  }

  // --- Interactions ---------------------------------------------------------
  function onSwatchActivate(swatch, persist) {
    var card = getCard(swatch);
    if (!card) return;
    var img = swatch.getAttribute('data-variant-image');
    if (img) applyImage(card, img, persist);
    if (persist) {
      setActive(swatch, '.kt-product-variant__item--current');
      var s = state(card);
      s.selColor = swatch.getAttribute('data-color') || s.selColor;
      updateSizeAvailability(card);
      if (s.selSize) applyResolvedVariant(card);
      else updateCardLinks(card, swatch.getAttribute('data-variant-url'));
      card.dataset.swatchLocked = '1';
    }
  }

  function onSizeSelect(pill) {
    if (pill.hasAttribute('disabled') || pill.classList.contains('kt-card-size-pill--soldout')) return;
    var card = getCard(pill);
    if (!card) return;
    setActive(pill, '.kt-card-size-pill');
    var s = state(card);
    s.selSize = pill.getAttribute('data-size');
    applyResolvedVariant(card);
  }

  document.addEventListener('click', function (e) {
    var swatch = closest(e.target, '.kt-product-color-swatch[data-color]');
    if (swatch) { e.preventDefault(); onSwatchActivate(swatch, true); return; }

    var pill = closest(e.target, '.kt-card-size-pill');
    if (pill) { e.preventDefault(); onSizeSelect(pill); return; }

    var atc = closest(e.target, '[data-card-atc]');
    if (atc) { e.preventDefault(); addToCart(atc); return; }

    var more = closest(e.target, '.kt-product-color-swatch__more');
    if (more) {
      e.preventDefault();
      var wrap = closest(more, '.kt-product-variant__list');
      if (wrap) wrap.querySelectorAll('.kt-swatch-hidden').forEach(function (n) { n.classList.remove('kt-swatch-hidden'); });
      more.setAttribute('aria-expanded', 'true');
      more.remove();
    }
  });

})();
